import { ofType } from 'redux-observable';
import { Subject, EMPTY, concat, Observable, of, isObservable, merge, BehaviorSubject } from 'rxjs';
import { catchError, mergeMap, switchMap, takeUntil, take } from 'rxjs/operators';

import { ModalService } from 'ui-components/services/modal-service';

import { LabelBatchStatusContainer } from '../../../components/modals/label-batch-status/processed-batch.container';
import { LabelBatchStatusStep } from '../../../enums/label-batch-status-step';
import { BulkErrorsAndWarningsService } from '../../../services/bulk-errors-and-warnings-service';
import { BulkPurchaseNotificationService } from '../../../services/bulk-purchase-noto-service/';
import Global from '../../../store/global-selectors';
import { ORDER_DETAILS_MODAL_CLOSED } from '../../../store/orders/order-details/orders.details.actions';
import {
  determineShouldShowErrorsOrWarnings,
  determineShouldShowCostReviewByError,
  determineShouldShowLabelBatchStatus,
} from '../../quickship/quickship.helpers';
import * as BulkActions from '../bulk.actions';
import * as BulkApi from '../bulk.api';
import * as Actions from './bulk.purchase.actions';
import { VALIDATION_TIMEOUT_ERROR } from '../bulk-error-types';
import { requestAndGetValidationMessages } from '../../label-purchase-validation';

export const startBulkPurchaseValidation = ({
  action$,
  included,
  excluded,
  andPrint,
  getState,
  quickshipProps,
  pusherTimeoutMs = 10000,
}) => {
  if (included.length === 0) {
    return EMPTY;
  }

  const startValidationSubject = new BehaviorSubject({ isRetry: false });
  const onTimeoutSubject = new Subject();
  return startValidationSubject.pipe(
    mergeMap(({ isRetry, jobGuids: initialJobGuids }) => {
      const initialValidationJobId = initialJobGuids && initialJobGuids[0];
      return requestAndGetValidationMessages({
        curriedApi: () => BulkApi.validate2(included),
        action$,
        isRetry,
        onRetryJob: () => startValidationSubject.next({ isRetry: true }),
        onValidationTimeout: () => onTimeoutSubject.next(BulkActions.bulkSetLabelProcessingEror(VALIDATION_TIMEOUT_ERROR)),
        pusherTimeoutMs,
        logContext: {
          includedFulfillmentPlanIdsLength: included.length,
          excludedFulfillmentPlanIdsLength: excluded.length,
          initialValidationJobId,
          purchasePoint: 'bulk',
        },
      }).pipe(
        switchMap(({ jobGuids, findMessage$ }) => {
          const [jobGuid] = jobGuids || [];
          const shouldShowErrorsOrWarnings = determineShouldShowErrorsOrWarnings(quickshipProps);
          const shouldMaybeShowCostReview = determineShouldShowCostReviewByError(undefined, quickshipProps);
          const shouldShowLabelBatchStatus = determineShouldShowLabelBatchStatus(quickshipProps);

          const nextActions = [
            // this value is used to update the reducer's jobId
            // it was being passed in recursively before my changes
            initialValidationJobId ? of(BulkActions.bulkValidateAssignNewJobId(initialValidationJobId, jobGuid)) : EMPTY,
            of(BulkActions.validateSuccess(jobGuid, included, excluded, andPrint)),
            of(BulkActions.bulkSetActiveLabelBatchStatusStep({ jobGuid })),
          ];

          const modal$ = new Observable((observer) => {
            const quickshipShouldOpenModal =
              !quickshipProps.shouldUseQuickship || shouldShowErrorsOrWarnings || shouldMaybeShowCostReview || shouldShowLabelBatchStatus;
            if (!Global.selectBulkLbsModalId(getState()) && quickshipShouldOpenModal) {
              const modalId = ModalService.launchModal({
                component: LabelBatchStatusContainer,
                onClose: () => {
                  observer.next(of(Actions.setLbsModalId('')));
                  observer.next(of(Actions.endBulkPurchase()));
                  observer.next(of(Actions.bulkClearActiveModalStep()));
                },
                props: {
                  jobGuid,
                },
              });
              observer.next(of(Actions.setLbsModalId(modalId)));
            }
          });

          return merge(
            onTimeoutSubject,
            findMessage$.pipe(
              take(1),
              mergeMap((pusherMessages) => {
                const pusherData = pusherMessages.map((m) => m.data);
                const erroredOrWarnedFulfillmentPlans = BulkErrorsAndWarningsService.erroredOrWarnedFulfillmentPlansFromJob({
                  messages: pusherData,
                });
                const erroredFulfillmentPlanIds =
                  BulkErrorsAndWarningsService.uniqueErroredOrWarnedFulfillmentPlanIds(erroredOrWarnedFulfillmentPlans);
                const state = getState();
                const activeModalStep = Global.selectBulkActiveModalStep(state);
                const job =
                  activeModalStep && activeModalStep.jobGuid
                    ? Global.selectBulkJobByJobGuid(state, {
                        jobGuid: activeModalStep.jobGuid,
                      })
                    : Global.selectBulkJobByJobGuid(state, { jobGuid });

                const errorCount = erroredFulfillmentPlanIds.length + excluded.length;

                if (errorCount && shouldShowErrorsOrWarnings) {
                  const orders = Global.selectEntitiesSalesOrdersByFulfillmentPlanIds(state, { fulfillmentPlanIds: job.included });
                  const fulfillmentPlans = Global.selectEntitiesFulfillmentPlansByIds(state, {
                    fulfillmentPlanIds: job.included,
                  });

                  const jobDetails = BulkErrorsAndWarningsService.getBulkJobDetails({
                    excluded,
                    erroredOrWarnedFulfillmentPlans,
                    erroredFulfillmentPlanIds,
                    andPrint,
                    jobGuid,
                    orders,
                    fulfillmentPlans,
                  });

                  return concat(
                    of(
                      BulkActions.bulkUpdateJobErrorAndWarnings(jobGuid, [...jobDetails.errors, ...jobDetails.warnings]),
                      BulkActions.bulkSetActiveLabelBatchStatusStep({
                        step: LabelBatchStatusStep.ErrorOrWarning.ordinal,
                      }),
                    ),
                  );
                }

                return concat(
                  of(Actions.endValidate(included, excluded, erroredFulfillmentPlanIds, andPrint)),
                  of(BulkActions.clearBatchValidated(jobGuid)),
                );
              }),
            ),
            modal$,
            ...nextActions,
          ).pipe(
            mergeMap((next$) => (isObservable(next$) ? next$ : of(next$))),
            takeUntil(action$.pipe(ofType(Actions.BULK_CLEAR_ACTIVE_MODAL_STEP))),
          );
        }),
      );
    }),
    // shut off here when final found
    catchError((e) => {
      BulkPurchaseNotificationService.showGenericHttpNotification();
      return of(BulkActions.validateFailure(e));
    }),
  );
};

export const waitForEndOfValidation = (action$) => {
  return action$.pipe(ofType(Actions.BULK_PURCHASE_VALIDATE_END, Actions.BULK_PURCHASE_COST_REVIEW_START, Actions.COST_REVIEW_BULK_RETRY));
};

export const retryValidation = (action$) => {
  return action$.pipe(ofType(ORDER_DETAILS_MODAL_CLOSED, Actions.BULK_PURCHASE_RETRY_VALIDATION));
};
